<template>
  <div>
    <div
      v-if="!images?.length"
      class="h-full w-full flex items-center rounded transition-300 border-2 border-dashed border-gray-800 hover:border-gray-400/50 cursor-pointer"
      :class="[
        {
          'border border-dashed border-gray-100': !images?.length,
          '!border-red': error,
          'border-green': dragging && !images?.length,
          'hover:border-gray-800 !border-gray-800 !cursor-not-allowed':
            images?.length,
        },
      ]"
      @dragover.prevent="handleDragOver"
      @drop.prevent="handleDrop"
      @dragenter.prevent="handleDragEnter"
      @dragleave.prevent="handleDragLeave"
    >
      <input
        id="file"
        type="file"
        name="file"
        class="w-0 h-0 absolute"
        accept="image/png, image/jpeg"
        multiple
        @change="handleFile"
        :disabled="images?.length"
      />
      <div
        class="w-full h-full py-3 backdrop-blur bg-gray-150 flex items-center justify-center flex-col gap-5"
        :class="{
          '!opacity-60': images?.length,
        }"
        @click="getFile"
      >
        <slot>
          <div class="text-base flex-center flex items-center flex-col">
            <i class="icon-doc-text text-green text-[32px]"></i>
            <p class="mt-4 text-sm leading-130 font-medium text-dark-100">
              {{ $t("drop_files") }}
            </p>
            <span class="text-gray leading-130 text-xs mx-1">
              {{ $t("choose_text") }}
              <span class="text-green">{{ $t("select_file") }}</span>
            </span>
          </div>
        </slot>
      </div>
    </div>
    <div
      class="flex gap-3 p-2 pr-3 mt-2 flex-col rounded-lg border border-solid border-gray-800"
      v-if="images.length"
    >
      <div
        class="w-full flex-y-center justify-between relative"
        v-for="(item, index) in images"
        :key="index"
      >
        <div class="flex-y-center gap-2">
          <div class="w-8 h-8 flex-center rounded-lg bg-green-100 shrink-0">
            <i class="icon-document-text text-green text-2xl" />
          </div>
          <div>
            <p class="text-xs leading-130 text-dark font-medium">
              {{ item?.name }}
            </p>
            <p class="text-xs leading-130 font-normal text-gray">
              {{ getFileSize(item?.file) }}
            </p>
          </div>
        </div>
        <i
          class="icon-close text-gray text-2xl transition-all hover:text-red-500 cursor-pointer"
          @click="removeImage(index)"
        ></i>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { defineEmits, defineProps, reactive, ref, watch } from "vue";
import { useCustomToast } from "@/composables/useCustomToast";
import { useI18n } from "vue-i18n";

const { showToast } = useCustomToast();
const { t } = useI18n();
const emit = defineEmits(["change"]);
interface Props {
  item: string;
  small: boolean;
  error: boolean;
  desc: string;
  defaultImages?: string[];
}
type image = {
  url: string;
  name: string;
  file: File;
  type: string;
};
const props = defineProps<Props>();
const images = reactive<image[]>([]);
const uploadType = ref("");

const file = ref();
const handleFile = async (event: Event) => {
  const target = event?.target as HTMLInputElement | null;
  if (target?.files === null) return;
  if (target?.files?.length) {
    if (target?.files[0]?.size / 1024 >= 2048) {
      showToast(t("wallet_file_upload_error"), "warning");
    } else {
      file.value = target?.files[0];
      for (let key in target?.files) {
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            resolve(reader.result);
          };
          reader.readAsDataURL(target?.files[key]);
          reader.onerror = (error) => reject(error);
        })
          .then((res) => {
            //Do not touch this, or else You will find yourself DEAD!!!
            images.push({
              url: res as string,
              name: target?.files[key].name,
              file: target?.files[key],
              type: "image",
            });
          })
          .catch((err) => {
            return new Error(err);
          });
      }
    }
  }
  send();
};

const handleDrop = async (event: DragEvent) => {
  if (!images.length) {
    event.preventDefault();
    dragging.value = false;
    uploadType.value = "create";

    if (event.dataTransfer?.items) {
      for (let i = 0; i < event.dataTransfer.items.length; i++) {
        if (event.dataTransfer.items[i].kind === "file") {
          file.value = event.dataTransfer.items[i]?.getAsFile();
          const reader = new FileReader();
          reader.onload = () => {
            const url = reader.result as string;

            images.push({
              url: url,
              name: file.value?.name || "", // Provide a default value for name
              file: file.value || null, // Provide a default value for file
              type: "image",
            });
          };

          reader.readAsDataURL(file.value);
        }
      }
    }

    send();
  }
};
const getFile = () => {
  const input = document.getElementById("file");
  input?.click();
};

const currentTarget = ref(null);
const dragging = ref(false);

const handleDragOver = (event: Event) => {
  event.preventDefault();
};

const handleDragEnter = (e) => {
  dragging.value = true;
  currentTarget.value = e?.target;
};

const handleDragLeave = (e) => {
  if (e?.target === currentTarget.value) {
    currentTarget.value = null;
    dragging.value = false;
  }
};
const removeImage = (index: number) => {
  images.splice(index, 1);
  send();
};

const getFileSize = (file: File | undefined): string => {
  if (!file) return "";

  const fileSizeInBytes = file.size;
  const fileSizeInKB = fileSizeInBytes / 1024;
  return fileSizeInKB.toFixed(2) + " KB";
};
function send() {
  emit("change", file);
}

watch(
  () => props.defaultImages,
  (value) => {
    if (value) {
      value.forEach((item) => {
        images.push({
          url: item,
          name: "",
          file: new File([], item),
          type: "image",
        });
      });
    }
  },
  {
    immediate: true,
    deep: true,
  },
);
</script>
<style></style>
